import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { IOrderManagementState } from "@/app_redux/reducers/slices/auth/order_management_slice";
import React, { useEffect, useState } from "react";

export default function ImgPreview({ data, imageitem }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageUrls, setImageUrls] = useState<{ [key: number]: string }>({});
  const [hiResUrls, setHiResUrls] = useState<{ [key: number]: string }>({});
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({ x: 50, y: 50 });

  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ORDER_MANAGEMENT", "id");
  const { routeState } = useRouteInfo(routeId);
  const { isUserPopupModalActive } = routeState.state as IOrderManagementState;

  useEffect(() => {
    const itemIndex = data?.findIndex(
      (item) => item?.vetic_variant_id === imageitem?.vetic_variant_id
    );
    setCurrentImageIndex(itemIndex >= 0 ? itemIndex : 0);
  }, [imageitem, data]);

  // Fetch & cache images once
  useEffect(() => {
    const fetchImage = async (url: string, setter: any, index: number) => {
      if (!url || setter[index]) return;
      try {
        const res = await fetch(url, { mode: "cors" });
        if (!res.ok) throw new Error("Fetch failed");
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        setter((prev: any) => ({ ...prev, [index]: objUrl }));
      } catch (e) {
        console.error("Error fetching image", e);
      }
    };

    const item = data?.[currentImageIndex];
    if (!item) return;

    fetchImage(item.thumbnail, setImageUrls, currentImageIndex);
    if (item.hires) fetchImage(item.hires, setHiResUrls, currentImageIndex);
  }, [currentImageIndex, data]);

  // Reset when modal closes
  useEffect(() => {
    if (!isUserPopupModalActive) {
      setIsPopupOpen(false);
      setMousePos({ x: 50, y: 50 });
    }
  }, [isUserPopupModalActive]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePos({ x, y });
  };

  // Use cached URLs to avoid blinking
  const thumbnailURL = imageUrls[currentImageIndex] || data[currentImageIndex]?.thumbnail;
  const zoomURL = hiResUrls[currentImageIndex] || data[currentImageIndex]?.hires || thumbnailURL;

  return (
    <div>
      {/* Thumbnail */}
      <div
        className="relative h-[7rem] w-[7rem] overflow-hidden rounded-xl cursor-zoom-in"
        onMouseEnter={() => setIsPopupOpen(true)}
        onMouseLeave={() => setIsPopupOpen(false)}
        onMouseMove={handleMouseMove}
      >
        <img
          src={thumbnailURL}
          alt={data[currentImageIndex]?.item_name || "image"}
          className="w-full h-full object-cover rounded-xl"
        />
      </div>

      {/* Popup */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 pointer-events-none">
          <div className="relative bg-white rounded-lg shadow-lg pointer-events-auto">
            <div
              className="h-[40rem] w-[40rem] overflow-hidden rounded-xl"
              onMouseMove={handleMouseMove}
            >
              <img
                src={zoomURL}
                alt="zoomed"
                className="w-full h-full object-cover transition-transform duration-75 will-change-transform"
                style={{
                  transform: "scale(2.5)",
                  transformOrigin: `${mousePos.x}% ${mousePos.y}%`,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}